import { useState } from "react";
import { Autocomplete, MenuItem, Stack, TextField, Typography } from "@mui/material";
import FiltroChip from "../kit/FiltroChip";
import { useGuiasTemas } from "../../queries/useTemas";
import { GuiaOpcion } from "../../types/tema";

/** Disponibilidad, por código de estado del tema. */
export const DISPONIBILIDAD = [
  { value: "disponible", label: "Disponible" },
  { value: "asignado", label: "Asignado" },
  { value: "pausado", label: "En pausa" },
  { value: "revision", label: "No disponible" },
];

export interface OpcionFiltro {
  value: string;
  label: string;
}

/**
 * Barra de filtros del catálogo: búsqueda, categoría, disponibilidad y —opcional— guía.
 * Es controlada: no sabe si quien la usa filtra en memoria ("Mis temas") o en el
 * servidor (el catálogo), solo emite los cambios. La comparten el catálogo de la
 * app y el público, para que buscar sea igual dentro y fuera.
 *
 * El filtro de guía se pasa como `onGuia`; sin él la columna no se muestra (en
 * "Mis temas" no tiene sentido, el guía es siempre la misma persona). El buscador
 * funciona igual: aparece solo donde se pase `onBusqueda`.
 *
 * `disponibilidades` acota las opciones de estado: el catálogo público solo
 * muestra disponibles y asignados, y ofrecer "No disponible" ahí sería un filtro
 * que nunca devuelve nada.
 */
export default function FiltrosTemas({
  busqueda,
  onBusqueda,
  categorias,
  categoria,
  onCategoria,
  estado,
  onEstado,
  guia,
  onGuia,
  favoritos,
  onFavoritos,
  total,
  disponibilidades = DISPONIBILIDAD,
}: {
  busqueda?: string;
  onBusqueda?: (v: string) => void;
  categorias: OpcionFiltro[];
  categoria: string;
  onCategoria: (v: string) => void;
  estado: string;
  onEstado: (v: string) => void;
  guia?: GuiaOpcion | null;
  onGuia?: (v: GuiaOpcion | null) => void;
  favoritos?: boolean;
  onFavoritos?: (v: boolean) => void;
  total: number;
  disponibilidades?: OpcionFiltro[];
}) {
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
      {onBusqueda && (
        <TextField
          size="small"
          label="Buscar"
          placeholder="Palabras del título…"
          value={busqueda ?? ""}
          onChange={(e) => onBusqueda(e.target.value)}
          sx={{ minWidth: 240 }}
        />
      )}

      <TextField select size="small" label="Categoría" value={categoria} onChange={(e) => onCategoria(e.target.value)} sx={{ minWidth: 200 }}>
        <MenuItem value="">Todas</MenuItem>
        {categorias.map((c) => (
          <MenuItem key={c.value} value={c.value}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField select size="small" label="Disponibilidad" value={estado} onChange={(e) => onEstado(e.target.value)} sx={{ minWidth: 170 }}>
        <MenuItem value="">Todas</MenuItem>
        {disponibilidades.map((d) => (
          <MenuItem key={d.value} value={d.value}>
            {d.label}
          </MenuItem>
        ))}
      </TextField>

      {onGuia && <FiltroGuia valor={guia ?? null} onChange={onGuia} />}

      {/* Los que marqué con la estrella: se prende y se apaga, no es una opción más
          de otro filtro —se combina con todos ellos—. */}
      {onFavoritos && <FiltroChip label="★ Favoritos" activo={!!favoritos} onClick={() => onFavoritos(!favoritos)} />}

      <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
        {total} {total === 1 ? "tema" : "temas"}
      </Typography>
    </Stack>
  );
}

/**
 * Guía como campo de escritura con sugerencias: el backend busca ignorando
 * tildes y eñes, así que "nunez" encuentra a "Núñez". Por eso `filterOptions`
 * devuelve las opciones tal cual — refiltrarlas en el cliente reintroduciría la
 * comparación con tildes.
 */
function FiltroGuia({ valor, onChange }: { valor: GuiaOpcion | null; onChange: (v: GuiaOpcion | null) => void }) {
  const [texto, setTexto] = useState("");
  const { data, isFetching } = useGuiasTemas(texto);

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 230 }}
      options={data ?? []}
      value={valor}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v, reason) => reason === "input" && setTexto(v)}
      getOptionLabel={(o) => o.nombre}
      isOptionEqualToValue={(a, b) => a.rut === b.rut}
      filterOptions={(x) => x}
      loading={isFetching}
      noOptionsText="Sin coincidencias"
      renderInput={(params) => <TextField {...params} label="Guía" placeholder="Escribe un nombre…" />}
    />
  );
}
